import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Group, Image, Modal, Stack } from '@mantine/core';
import { captureVideoFrame } from '../../../media/videoPoster';
import { uploadWebImage } from '../../../requests/webMedia';

function CoverPicker({ src, onClose, onChange }) {
  const videoRef = useRef(null);
  const alive = useRef(true);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const capture = async () => {
    setBusy(true);
    setError('');
    try {
      videoRef.current.pause();
      const frame = await captureVideoFrame(videoRef.current);
      const media = await uploadWebImage(frame);
      if (!alive.current) return;
      onChangeRef.current(media.variants?.max?.url || media.source);
      onClose();
    } catch (failure) {
      if (alive.current) setError(failure.message || 'La couverture n’a pas pu être enregistrée.');
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  return (
    <Modal opened onClose={onClose} title="Choisir la couverture" size="lg" closeOnClickOutside={!busy} closeOnEscape={!busy} withCloseButton={!busy}>
      <Stack>
        <video
          ref={videoRef}
          crossOrigin="anonymous"
          src={src}
          controls
          playsInline
          preload="auto"
          style={{ width: '100%', maxHeight: '60vh' }}
          onLoadedData={() => setReady(true)}
          onSeeking={() => setReady(false)}
          onSeeked={() => setReady(true)}
          onError={() => { setReady(false); setError('La vidéo ne peut pas être chargée pour choisir sa couverture.'); }}
        />
        {error && <Alert color="red" role="alert">{error}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button onClick={capture} disabled={!ready} loading={busy}>Utiliser cette image</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default function VideoCoverField({ src, poster, automaticPoster, onChange }) {
  const [opened, setOpened] = useState(false);
  useEffect(() => setOpened(false), [src]);
  return (
    <Stack gap="xs">
      {(poster || automaticPoster) && <Image src={poster || automaticPoster} alt="Couverture de la vidéo" h={100} fit="contain" />}
      <Button size="xs" variant="default" onClick={() => setOpened(true)}>Choisir la couverture</Button>
      {poster && <Button size="xs" variant="subtle" onClick={() => onChange('')}>{automaticPoster ? 'Rétablir la couverture automatique' : 'Retirer la couverture'}</Button>}
      {opened && <CoverPicker key={src} src={src} onClose={() => setOpened(false)} onChange={onChange} />}
    </Stack>
  );
}
